import { useEffect, useState } from 'react';

import { de } from '../../i18n/de';
import { calibrationRepo } from '../../data/repositories';
import { useProfileStore } from '../../core/profile/ProfileStore';
import { inferFromTrials, type CalibrationProposal } from '../../core/calibration/inference';
import { Zieluebung } from '../Zieluebung/Zieluebung';
import { CareButton } from './BetreuerModus';
import type { CalibrationRun, CalibrationTrial } from '../../core/types';

/**
 * Run the Zielübung, then show what it found **as a proposal a caregiver accepts** (1.12).
 *
 * Never auto-applied, and that is the point rather than caution: this is 45 taps being used to draw a
 * conclusion about someone's vision and motor control, and a layout that rearranged itself on that
 * evidence would be the app deciding something it is not entitled to decide.
 *
 * Rejecting still stores the run. The data is the value — Phase 3 §3.4 reviews accumulated runs
 * against the real usage heatmap — so a rejected proposal is evidence, not waste.
 */
export function CalibrationPanel(): React.JSX.Element {
  const update = useProfileStore((s) => s.update);
  // Only so a wide-gap proposal widens the board the person already has instead of handing them
  // QWERTZ — see `wideGapLayout`.
  const keyboardLayout = useProfileStore((s) => s.settings.keyboardLayout);
  const [running, setRunning] = useState(false);
  const [proposal, setProposal] = useState<CalibrationProposal | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [history, setHistory] = useState<CalibrationRun[]>([]);

  const reload = (): void => void calibrationRepo.all().then(setHistory);
  useEffect(reload, []);

  const finish = (trials: CalibrationTrial[]): void => {
    setRunning(false);
    const inferred = inferFromTrials(trials, keyboardLayout);
    setProposal(inferred);

    const id = `cal-${Date.now()}`;
    setRunId(id);
    // Stored before the caregiver decides: the trials are the record, and an unanswered proposal
    // must not be able to lose them.
    void calibrationRepo
      .upsert({
        id,
        ranAt: new Date().toISOString(),
        trials,
        derivedSettings: inferred.derivedSettings,
        recommendations: inferred.recommendations,
        provisional: inferred.provisional,
        accepted: false,
      })
      .then(reload);
  };

  if (running) {
    return <Zieluebung onFinish={finish} onCancel={() => setRunning(false)} />;
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}>
      <h2 style={{ fontWeight: 700 }}>{de.calibration}</h2>

      <CareButton
        id="care:run-calibration"
        label={de.calibrationStart}
        onPress={() => {
          setProposal(null);
          setRunning(true);
        }}
      />

      {proposal ? (
        <div
          data-testid="calibration-proposal"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 12,
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        >
          <strong>{de.calibrationProposal}</strong>
          {proposal.provisional ? (
            <p data-testid="calibration-provisional">{de.calibrationProvisional}</p>
          ) : null}

          <ul style={{ paddingLeft: 20 }}>
            {proposal.recommendations.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>

          {Object.keys(proposal.derivedSettings).length === 0 ? (
            <p>{de.calibrationNoChange}</p>
          ) : (
            <dl
              data-testid="calibration-settings"
              style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px' }}
            >
              {Object.entries(proposal.derivedSettings).map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt>{key}</dt>
                  <dd>{JSON.stringify(value)}</dd>
                </div>
              ))}
            </dl>
          )}

          {/* The numbers behind the conclusion, so it can be checked rather than trusted. */}
          <details>
            <summary style={{ minHeight: 'var(--min-target)' }}>Messwerte</summary>
            <p>Versuche: {proposal.summary.completed}</p>
            <p>
              Fehltreffer je Grösse:{' '}
              {proposal.summary.missRateBySize
                .map((s) => `${s.targetMm} mm ${Math.round(s.missRate * 100)} %`)
                .join(' · ')}
            </p>
            <p>
              Fehltreffer je Spalte:{' '}
              {proposal.summary.missRateByColumn.map((r) => `${Math.round(r * 100)} %`).join(' · ')}
            </p>
            <p>Nie berührt: {proposal.summary.neverTouched}</p>
          </details>

          <div style={{ display: 'flex', gap: 8 }}>
            <CareButton
              id="calibration:accept"
              label={de.calibrationAccept}
              onPress={() => {
                void update(proposal.derivedSettings);
                if (runId) {
                  const stored = history.find((run) => run.id === runId);
                  void calibrationRepo
                    .upsert({
                      ...(stored ?? {
                        id: runId,
                        ranAt: new Date().toISOString(),
                        trials: [],
                        recommendations: proposal.recommendations,
                        provisional: proposal.provisional,
                      }),
                      id: runId,
                      derivedSettings: proposal.derivedSettings,
                      accepted: true,
                    } as CalibrationRun)
                    .then(reload);
                }
                setProposal(null);
              }}
            />
            <CareButton
              id="calibration:reject"
              label={de.calibrationReject}
              onPress={() => setProposal(null)}
            />
          </div>
        </div>
      ) : null}

      {history.length > 0 ? (
        <p data-testid="calibration-history">
          {history.length} Durchgänge aufgezeichnet, letzter{' '}
          {history.at(-1)?.ranAt.slice(0, 10) ?? ''}
        </p>
      ) : null}
    </section>
  );
}
